// Lógica del formulario de búsqueda nuevo (pages/General/FormularioNuevo.vue).
//
// Mismo contrato que Formulario.vue (el viejo, que queda como respaldo):
//  - GET  partners
//  - GET  intake/progress?session_id(&meta&email)  → restaurar
//  - POST intake/progress                          → autoguardado (debounce 1,2 s)
//  - POST intake                                   → envío (mismo payload)
//  - POST intake/{id}/matching                     → preselección
//  - POST intake/progress { page: 'thanks', is_final: true }
//  - localStorage: hyf_form_session, hyf_form_progress_${session}
//  - ?meta=… / cualquier otro parámetro → form.meta ; ?page=input|thanks|N
import axios from 'axios'
import HyFLoader from '@/components/Loader/HyFLoader.vue'
import NfCombo from '@/components/Formulario/NfCombo.vue'
import NfFlag from '@/components/Formulario/NfFlag.vue'
import NfSegmented from '@/components/Formulario/NfSegmented.vue'
import '@/components/Formulario/formularioNuevo.css'
import {
    PHONE_COUNTRIES, ORIGIN_COUNTRIES, COUNTRY_FLAGS, COUNTRY_FALLBACK, CITY_GROUPS, CITY_LABELS,
    ZONES, PROGRAMS, ACCOMMODATION, PEOPLE, URGENCY, ICONS, TOTAL_STEPS, STEP_FIELDS,
    emptyForm, normalizeForm,
} from '@/components/Formulario/nfData'

const API = () => process.env.VUE_APP_API_URL
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/
const TEXT_FIELDS = ['nombre', 'email', 'whatsapp', 'edad', 'universidad', 'universidad_destino']
const SAVE_DEBOUNCE_MS = 1200
const LOADER_MIN_MS = 2900      // la carga se ve al menos una vuelta de los aviones
const LOADING_MSG_MS = 1900
const BUDGET_MIN = 100
const BUDGET_MAX = 3000

const wait = (ms) => new Promise((r) => setTimeout(r, ms))
const todayISO = () => {
    const d = new Date()
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}
const parseISO = (s) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d) }
const generateSessionId = () => 'hyf_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now().toString(36)

export default {
    name: 'FormularioNuevo',
    components: { HyFLoader, NfCombo, NfFlag, NfSegmented },

    data() {
        return {
            TOTAL_STEPS, PROGRAMS, ACCOMMODATION, URGENCY, ICONS,
            form: emptyForm(),
            step: 1,
            visibleStep: 1,
            errors: {},
            valid: {},
            touched: {},
            partners: [],
            partnerAutoHecho: false,
            sessionId: null,
            progressLocalKey: null,
            pageLabel: null,
            ready: false,
            animating: false,
            submitting: false,
            editing: false,
            finished: false,
            screen: 'form', // form | loading | success | error
            saveState: '',  // '' | saving | saved
            toastVisible: false,
            cheered: {},
            bubble: '',
            bubbleKey: 0,
            shownPct: 0,
            pctBump: false,
            planeMoving: false,
            planeTakeoff: false,
            barHidden: false,
            loaderMinHeight: 460,
            loadingIdx: 0,
            successName: '',
            lastPct: -1,
        }
    },

    computed: {
        today() { return todayISO() },
        locale() { return this.$i18n.locale },

        // ── Opciones de los combos (con banderas) ─────────────────────
        phoneOptions() {
            let names = null
            try { names = new Intl.DisplayNames([this.locale, 'es'], { type: 'region' }) } catch (e) { names = null }
            return PHONE_COUNTRIES.map((c) => {
                let label = ''
                try { label = names ? names.of(c.code) : '' } catch (e) { label = '' }
                if (!label || label === c.code) label = c.name.replace(/\s*\(\+\d+\)\s*$/, '')
                return { value: c.code, label, meta: '+' + c.value, flag: c.code, dial: c.value }
            })
        },
        originOptions() {
            // value = nombre del país en el idioma actual: es lo que viaja en
            // pais_origen, igual que en Formulario.vue.
            return ORIGIN_COUNTRIES.map((k) => ({ value: this.countryLabel(k), label: this.countryLabel(k), flag: COUNTRY_FLAGS[k] || '' }))
        },
        cityOptions() {
            const out = []
            CITY_GROUPS.forEach((g) => g.cities.forEach((c) => out.push({
                value: c, label: this.cityLabel(c), group: this.countryLabel(g.country), flag: COUNTRY_FLAGS[g.country],
            })))
            return out
        },

        zones() { return ZONES[this.form.ciudad] || [] },
        nearbyZones() { return this.zones.filter((z) => !this.form.zonas_preferidas.includes(z)) },
        showNearby() { return this.form.zonas_preferidas.length > 0 && this.nearbyZones.length > 0 },
        showPartnerInput() { return !this.partners.length || this.form.partner_otro },

        peopleItems() { return PEOPLE.map((n) => ({ value: n, label: this.t('ops.people.' + n) })) },
        coupleItems() { return [{ value: true, label: this.t('ops.couple_yes') }, { value: false, label: this.t('ops.couple_no') }] },
        inCityItems() { return [{ value: true, label: this.t('ops.in_city_yes') }, { value: false, label: this.t('ops.in_city_no') }] },
        ticketsItems() { return [{ value: true, label: this.t('ops.tickets_yes') }, { value: false, label: this.t('ops.tickets_no') }] },

        durationText() {
            const { fecha_inicio: a, fecha_fin: b } = this.form
            if (!a || !b || b < a) return ''
            const days = Math.max(1, Math.round((parseISO(b) - parseISO(a)) / 86400000))
            if (days < 30) return this.t(days === 1 ? 'duration.day' : 'duration.days', { n: days })
            const months = days / 30.44
            const m = months.toLocaleString(this.locale, { maximumFractionDigits: 1 })
            return this.t(months < 1.05 ? 'duration.month' : 'duration.months', { m, d: days })
        },

        // ── Presupuesto: número grande + slider ───────────────────────
        rangeValue() {
            const v = Number(this.form.presupuesto) || BUDGET_MIN
            return Math.min(Math.max(v, BUDGET_MIN), BUDGET_MAX)
        },
        rangePct() { return ((this.rangeValue - BUDGET_MIN) / (BUDGET_MAX - BUDGET_MIN)) * 100 },
        budgetWidth() { return Math.max(3, String(this.form.presupuesto || '').length) + 0.4 + 'ch' },

        // ── Barra de progreso: avanza también dentro del paso (0–90 %) ─
        progressPct() {
            if (this.planeTakeoff || this.screen !== 'form') return 100
            const fields = STEP_FIELDS[this.step]
            const ok = fields.filter((n) => !this.message(n)).length
            return Math.round((((this.step - 1) + ok / fields.length) / TOTAL_STEPS) * 90)
        },

        isLast() { return this.step === TOTAL_STEPS },
        nextLabel() {
            if (this.isLast) return this.t('nav.submit')
            return this.t(this.editing ? 'nav.to_review' : 'nav.continue')
        },

        // ── Resumen lateral ────────────────────────────────────────────
        summaryItems() {
            const f = this.form
            const items = []
            if (f.ciudad) items.push(['📍', this.cityLabel(f.ciudad)])
            if (f.fecha_inicio && f.fecha_fin && f.fecha_fin >= f.fecha_inicio) items.push(['🗓', `${this.fmtDate(f.fecha_inicio)} → ${this.fmtDate(f.fecha_fin)}`])
            if (f.tipo_alojamiento) {
                items.push(['🏠', this.t(`ops.accommodation.${f.tipo_alojamiento}.label`)])
                const n = Number(f.cantidad_personas)
                items.push(['👤', n >= 4 ? this.t('summary.people_max') : n > 1 ? this.t('summary.people', { n }) : this.t('summary.solo')])
            }
            if (Number(f.presupuesto) >= BUDGET_MIN) items.push(['💵', this.t('summary.per_month', { n: this.fmtNum(f.presupuesto) })])
            return items
        },

        // ── Revisión final (paso 5) ────────────────────────────────────
        reviewRows() {
            const f = this.form
            const zonas = f.zonas_preferidas.length
                ? ` · ${f.zonas_preferidas.slice(0, 2).join(', ')}${f.zonas_preferidas.length > 2 ? '…' : ''}` : ''
            return [
                { key: 'name', value: f.nombre, step: 1 },
                { key: 'whatsapp', value: f.phone_code ? `+${f.phone_code} ${f.whatsapp}` : f.whatsapp, step: 1 },
                { key: 'destination', value: f.ciudad ? this.cityLabel(f.ciudad) + zonas : '', step: 2 },
                { key: 'dates', value: f.fecha_inicio && f.fecha_fin ? `${this.fmtDate(f.fecha_inicio)} → ${this.fmtDate(f.fecha_fin)}` : '', step: 2 },
                { key: 'program', value: f.programa ? this.t('ops.program.' + f.programa) : '', step: 2 },
                { key: 'accommodation', value: f.tipo_alojamiento ? this.t(`ops.accommodation.${f.tipo_alojamiento}.label`) : '', step: 3 },
                { key: 'budget', value: f.presupuesto ? `USD ${this.fmtNum(f.presupuesto)} ${this.t('labels.per_month')}` : '', step: 4 },
                { key: 'urgency', value: f.urgencia ? this.t(`ops.urgency.${f.urgencia}.label`) : '', step: 4 },
            ]
        },

        loadingMessage() { return this.t('loading.msg' + (this.loadingIdx + 1)) },
    },

    watch: {
        form: {
            deep: true,
            handler() { if (this.ready && !this.finished && !this.submitting) this.scheduleSave() },
        },
        progressPct(p) { this.setProgress(p) },
        // Último paso: si el link traía un código igual a un partner, se marca solo.
        step(n) { if (n === TOTAL_STEPS) this.autoPartnerFromCode() },
        // El país de origen viaja con su nombre traducido: si cambia el idioma,
        // se traduce también lo ya elegido para que el combo lo siga mostrando.
        locale(nuevo, viejo) {
            if (!this.form.pais_origen) return
            const k = ORIGIN_COUNTRIES.find((c) => this.countryLabel(c, viejo) === this.form.pais_origen)
            if (k) this.form.pais_origen = this.countryLabel(k, nuevo)
            document.title = this.t('page_title')
        },
    },

    async mounted() {
        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        this.finePointer = window.matchMedia('(pointer: fine)').matches
        this.prevTitle = document.title
        document.title = this.t('page_title')
        document.documentElement.classList.add('nf-page')
        // Fuente del diseño (se carga una sola vez).
        if (!document.querySelector('link[data-nf-font]')) {
            const link = document.createElement('link')
            link.rel = 'stylesheet'
            link.href = 'https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&display=swap'
            link.setAttribute('data-nf-font', '')
            document.head.appendChild(link)
        }
        this.measureHeader()
        window.addEventListener('resize', this.measureHeader, { passive: true })
        document.addEventListener('keydown', this.onDocKey)
        window.addEventListener('beforeunload', this.beforeUnloadHandler)

        this.loadPartners()

        // Parámetro de campaña, igual que Formulario.vue.
        const q = (this.$route && this.$route.query) || {}
        const one = (v) => (Array.isArray(v) ? v[0] : v)
        if (one(q.meta)) {
            const s = String(one(q.meta)).trim()
            this.form.meta = s.includes('-') ? s : `meta-${s}`
        } else {
            for (const key of Object.keys(q)) {
                if (key === 'page' || key === 'meta') continue
                const val = one(q[key])
                if (val === undefined || val === null || String(val).trim() === '') continue
                this.form.meta = `${key}-${String(val).trim()}`
                break
            }
        }
        const pageParam = one(q.page) ? String(one(q.page)) : null

        try {
            const stored = localStorage.getItem('hyf_form_session')
            this.sessionId = stored || generateSessionId()
            if (!stored) localStorage.setItem('hyf_form_session', this.sessionId)
        } catch (e) {
            this.sessionId = generateSessionId()
        }
        this.progressLocalKey = `hyf_form_progress_${this.sessionId}`

        // Marketing → Partners: entró con un código en el link. La API solo lo
        // anota si el código es un partner (una vez por sesión). Sin esperar.
        if (this.form.meta) {
            axios.post(`${API()}marketing/evento`, { tipo: 'form_codigo', meta: this.form.meta, session_id: this.sessionId }).catch(() => {})
        }
        const restored = await this.restoreProgress()

        if (!this.form.phone_country) {
            const g = this.guessPhoneCountry()
            if (g) { this.form.phone_country = g.code; this.form.phone_code = g.value }
        }

        if (pageParam) {
            if (pageParam === 'input') { this.pageLabel = 'input'; this.step = 1 }
            else if (pageParam === 'thanks') this.screen = 'success'
            else if (!Number.isNaN(parseInt(pageParam, 10))) this.step = Math.min(TOTAL_STEPS, Math.max(1, parseInt(pageParam, 10)))
            this.setPageQuery(pageParam)
        } else if (!restored) {
            this.pageLabel = 'input'
            this.step = 1
            this.setPageQuery('input')
        }
        this.visibleStep = this.step

        if (restored && this.screen === 'form') {
            for (let i = 1; i <= this.step; i++) this.$set(this.cheered, i, true) // sin festejos repetidos
            this.toastVisible = true
            this.toastTimer = setTimeout(() => { this.toastVisible = false }, 9000)
        }

        this.setProgress(this.progressPct, true)
        await this.$nextTick()
        this.ready = true
        if (restored || this.form.meta) this.scheduleSave()
    },

    beforeDestroy() {
        // Si quedaba un guardado pendiente, se manda ahora.
        if (this.saveTimer) { clearTimeout(this.saveTimer); this.saveTimer = null; if (!this.finished) this.saveProgress().catch(() => {}) }
        clearTimeout(this.toastTimer); clearTimeout(this.bubbleTimer); clearTimeout(this.savedTimer)
        clearInterval(this.loadingTimer)
        cancelAnimationFrame(this.pctRaf)
        window.removeEventListener('resize', this.measureHeader)
        document.removeEventListener('keydown', this.onDocKey)
        window.removeEventListener('beforeunload', this.beforeUnloadHandler)
        document.documentElement.classList.remove('nf-page')
        document.title = this.prevTitle
    },

    methods: {
        // ── Utilidades ─────────────────────────────────────────────────
        t(key, params) { return this.$t('formulario_nuevo.' + key, params) },
        fmtDate(s) { return s ? parseISO(s).toLocaleDateString(this.locale, { day: 'numeric', month: 'short' }) : '' },
        fmtNum(n) { return Number(n).toLocaleString(this.locale) },
        countryLabel(k, locale) {
            const path = `formulario.countries.${k}`
            const t = this.$t(path, locale || this.locale)
            if (t && t !== path) return t
            if (COUNTRY_FALLBACK[k]) return COUNTRY_FALLBACK[k]
            return k.replace(/_/g, ' ').split(' ').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
        },
        cityLabel(k) {
            const path = `formulario.cities.${k}`
            const t = this.$t(path)
            if (t && t !== path) return t
            return CITY_LABELS[k] || k
        },
        measureHeader() {
            const h = document.querySelector('.app-header')
            const top = h && getComputedStyle(h).position !== 'static' ? Math.round(h.getBoundingClientRect().height) : 0
            this.$el.style.setProperty('--nf-top', top + 'px')
        },
        guessPhoneCountry() {
            const langs = navigator.languages || [navigator.language || '']
            for (const l of langs) {
                const region = (String(l).split('-')[1] || '').toUpperCase()
                const c = PHONE_COUNTRIES.find((x) => x.code === region)
                if (c) return c
            }
            return null
        },
        setPageQuery(page) {
            const q = Object.assign({}, this.$route.query || {}, { page: String(page) })
            if (String((this.$route.query || {}).page) === String(page)) return
            this.$router.replace({ path: this.$route.path, query: q }).catch(() => {})
        },
        selectedMenuLanguage() {
            try {
                const fromI18n = this.$i18n && this.$i18n.locale ? this.$i18n.locale : null
                const fromStore = this.$store && this.$store.state ? this.$store.state.iso639_1 : null
                const fromLs = typeof localStorage !== 'undefined' ? localStorage.getItem('iso639_1') : null
                return fromI18n || fromStore || fromLs || null
            } catch (e) {
                return null
            }
        },

        // Efecto de onda al pulsar chips, tarjetas, opciones y el botón principal.
        ripple(e) {
            if (this.reduceMotion) return
            const host = e.target.closest && e.target.closest('.nf-chip, .nf-card, .nf-option, .nf-btn-primary')
            if (!host) return
            const r = host.getBoundingClientRect()
            const size = Math.max(r.width, r.height)
            const span = document.createElement('span')
            span.className = 'nf-ripple'
            span.style.cssText = `width:${size}px;height:${size}px;left:${e.clientX - r.left - size / 2}px;top:${e.clientY - r.top - size / 2}px`
            host.appendChild(span)
            setTimeout(() => span.remove(), 650)
        },

        // ── Validación (mismas reglas que Formulario.vue) ─────────────
        message(name) {
            const f = this.form
            const vacio = (v) => v === null || v === undefined || String(v).trim() === ''
            switch (name) {
                case 'nombre': return f.nombre.trim().length < 2 ? this.t('errors.name') : ''
                case 'email': return !f.email.trim() ? this.t('errors.email_required') : !EMAIL_RE.test(f.email.trim()) ? this.t('errors.email_invalid') : ''
                case 'whatsapp': return !f.phone_code ? this.t('errors.phone_code') : f.whatsapp.length < 6 ? this.t('errors.phone') : ''
                case 'pais_origen': return !f.pais_origen ? this.t('errors.origin') : ''
                case 'edad': return vacio(f.edad) ? this.t('errors.age_required') : (Number(f.edad) < 18 || Number(f.edad) > 100) ? this.t('errors.age_range') : ''
                case 'programa': return !f.programa ? this.t('errors.program') : ''
                case 'universidad': return !f.universidad.trim() ? this.t('errors.university') : ''
                case 'universidad_destino': return !f.universidad_destino.trim() ? this.t('errors.university_dest') : ''
                case 'ciudad': return !f.ciudad ? this.t('errors.city') : ''
                case 'zonas_preferidas': return this.zones.length && !f.zonas_preferidas.length ? this.t('errors.zones') : ''
                case 'zonas_cercanas': return this.showNearby && !f.zonas_cercanas.length ? this.t('errors.nearby') : ''
                case 'fecha_inicio': return !f.fecha_inicio ? this.t('errors.start_required') : f.fecha_inicio < todayISO() ? this.t('errors.start_past') : ''
                case 'fecha_fin': return !f.fecha_fin ? this.t('errors.end_required') : (f.fecha_inicio && f.fecha_fin < f.fecha_inicio) ? this.t('errors.end_before') : ''
                case 'tipo_alojamiento': return !f.tipo_alojamiento ? this.t('errors.accommodation') : ''
                case 'cantidad_personas': return !f.cantidad_personas ? this.t('errors.people') : ''
                case 'va_en_pareja': return f.cantidad_personas >= 2 && f.va_en_pareja === null ? this.t('errors.couple') : ''
                case 'presupuesto': return !f.presupuesto || Number(f.presupuesto) < BUDGET_MIN ? this.t('errors.budget') : ''
                case 'urgencia': return !f.urgencia ? this.t('errors.urgency') : ''
                case 'en_ciudad': return f.en_ciudad === null ? this.t('errors.choose') : ''
                case 'tiene_pasajes': return f.en_ciudad === false && f.tiene_pasajes === null ? this.t('errors.choose') : ''
                case 'partner_nombre': return f.partner_otro && !f.partner_nombre.trim() ? this.t('errors.partner_name') : ''
                default: return ''
            }
        },
        // Pinta el estado del campo. Sin `force` solo marca error si ya se tocó.
        paint(name, force) {
            const msg = this.message(name)
            const show = force || this.touched[name] || this.errors[name]
            if (msg && show) {
                this.$set(this.errors, name, msg)
                this.$set(this.valid, name, false)
            } else if (!msg) {
                this.$delete(this.errors, name)
                const v = this.form[name]
                this.$set(this.valid, name, TEXT_FIELDS.includes(name) && !!(force || this.touched[name]) && String(v == null ? '' : v).trim() !== '')
            }
            return msg
        },
        touch(name) { this.$set(this.touched, name, true); this.paint(name) },
        fieldClass(name) { return { 'is-invalid': !!this.errors[name], 'is-valid': !!this.valid[name] } },
        validateStep(step, show = true) {
            return STEP_FIELDS[step].filter((n) => (show ? this.paint(n, true) : this.message(n)))
        },
        focusFirstError(names) {
            const el = this.$el.querySelector(`[data-field="${names[0]}"]`)
            if (!el) return
            el.scrollIntoView({ behavior: this.reduceMotion ? 'auto' : 'smooth', block: 'center' })
            names.forEach((n) => {
                const f = this.$el.querySelector(`[data-field="${n}"]`)
                if (!f) return
                f.classList.remove('is-shake')
                void f.offsetWidth
                f.classList.add('is-shake')
            })
            const ctrl = el.querySelector('input:not([type=range]), textarea, .nf-combo-trigger, .nf-chip, .nf-card, .nf-seg-btn, .nf-option')
            if (ctrl) setTimeout(() => ctrl.focus({ preventScroll: true }), this.reduceMotion ? 0 : 380)
        },

        // ── Entradas ───────────────────────────────────────────────────
        onInput(key) {
            if (this.errors[key] || this.valid[key]) this.paint(key)
        },
        onBlur(key) {
            if (String(this.form[key] == null ? '' : this.form[key]).trim() === '' && !this.errors[key]) return
            this.touch(key)
        },
        onPhoneInput(e) {
            const digits = e.target.value.replace(/\D/g, '')
            if (digits !== e.target.value) e.target.value = digits
            this.form.whatsapp = digits
            this.onInput('whatsapp')
        },
        onPhoneCountry(code, o) {
            this.form.phone_country = code
            this.form.phone_code = o.dial
            this.$set(this.touched, 'whatsapp', true)
            if (this.form.whatsapp) this.paint('whatsapp')
            else this.$delete(this.errors, 'whatsapp')
            this.$nextTick(() => this.$refs.whatsapp && this.$refs.whatsapp.focus())
        },
        onOrigin(v) { this.form.pais_origen = v; this.touch('pais_origen') },
        onCity(v) {
            if (this.form.ciudad !== v) {
                this.form.ciudad = v
                this.form.zonas_preferidas = []
                this.form.zonas_cercanas = []
                this.$delete(this.errors, 'zonas_preferidas')
                this.$delete(this.errors, 'zonas_cercanas')
            }
            this.touch('ciudad')
        },
        onComboOpen(combo) {
            ['comboPhone', 'comboOrigin', 'comboCity'].forEach((r) => { const c = this.$refs[r]; if (c && c !== combo) c.close(false) })
        },
        closeCombos() {
            ['comboPhone', 'comboOrigin', 'comboCity'].forEach((r) => this.$refs[r] && this.$refs[r].close(false))
        },
        pick(key, value) { this.form[key] = value; this.touch(key) },
        pickAccommodation(o) {
            this.form.tipo_alojamiento = o.value
            this.form.tipo_alojamiento_tipo = o.tipo || ''
            this.form.tipo_alojamiento_categoria = typeof o.categoria !== 'undefined' ? o.categoria : null
            this.touch('tipo_alojamiento')
        },
        pickPeople(n) {
            this.form.cantidad_personas = n
            if (n < 2) this.form.va_en_pareja = null
            this.touch('cantidad_personas')
            this.$delete(this.errors, 'va_en_pareja')
        },
        pickInCity(v) {
            this.form.en_ciudad = v
            if (v === true) this.form.tiene_pasajes = null
            this.$delete(this.errors, 'tiene_pasajes')
            this.touch('en_ciudad')
        },
        toggleZone(z) {
            const arr = this.form.zonas_preferidas
            const i = arr.indexOf(z)
            i >= 0 ? arr.splice(i, 1) : arr.push(z)
            // Una zona no puede ser preferida y cercana a la vez.
            this.form.zonas_cercanas = this.form.zonas_cercanas.filter((x) => !arr.includes(x))
            this.touch('zonas_preferidas')
            if (this.errors.zonas_cercanas) this.paint('zonas_cercanas')
        },
        toggleNearby(z) {
            const arr = this.form.zonas_cercanas
            const i = arr.indexOf(z)
            i >= 0 ? arr.splice(i, 1) : arr.push(z)
            this.touch('zonas_cercanas')
        },
        onDatesChange(key) {
            const f = this.form
            // Si la salida quedó antes de la nueva llegada, se limpia.
            if (key === 'fecha_inicio' && f.fecha_fin && f.fecha_inicio && f.fecha_fin < f.fecha_inicio) f.fecha_fin = ''
            if (key === 'fecha_inicio' && f.fecha_inicio && !f.fecha_fin && this.finePointer) {
                setTimeout(() => { try { this.$refs.fechaFin && this.$refs.fechaFin.showPicker && this.$refs.fechaFin.showPicker() } catch (e) { /* no soportado */ } }, 250)
            }
            this.$set(this.touched, key, true)
            this.paint('fecha_inicio')
            if (f.fecha_fin || this.errors.fecha_fin) this.paint('fecha_fin')
        },
        onBudgetInput(e) {
            this.form.presupuesto = e.target.value === '' ? '' : Number(e.target.value)
            if (this.errors.presupuesto) this.paint('presupuesto')
        },
        onRangeInput(e) {
            this.form.presupuesto = Number(e.target.value)
            this.touch('presupuesto')
        },
        onBudgetBlur() { if (this.form.presupuesto !== '') this.touch('presupuesto') },
        blurOnWheel(e) { e.target.blur() },

        // ── Partners ───────────────────────────────────────────────────
        async loadPartners() {
            try {
                const { data } = await axios.get(`${API()}partners`)
                this.partners = data && Array.isArray(data.partners) ? data.partners : []
            } catch (e) {
                this.partners = []
            }
            this.adjustPartnerOther()
            if (this.step === TOTAL_STEPS) this.autoPartnerFromCode()
        },
        // Link de "Generar link exclusivo" (?meta=EADA → form.meta = "meta-EADA"):
        // si el código coincide con un partner activo, se elige ese partner.
        // Sin distinguir mayúsculas, tildes, espacios ni símbolos. Solo una vez
        // por carga y solo si la persona no eligió nada, para no pisar su elección.
        autoPartnerFromCode() {
            const f = this.form
            if (this.partnerAutoHecho || !f.meta || !this.partners.length) return
            if (f.partner_otro || (f.partner_nombre || '').trim()) return
            const norm = (s) => String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]/g, '')
            const meta = String(f.meta)
            const candidatos = [meta.slice(meta.indexOf('-') + 1), meta].map(norm).filter(Boolean)
            const p = this.partners.find((x) => candidatos.includes(norm(x.nombre)))
            if (!p) return
            this.partnerAutoHecho = true
            this.pickPartner(p.nombre)
        },
        // Lo guardado con un nombre que no está en la lista se reabre como "Otro".
        adjustPartnerOther() {
            const nombre = (this.form.partner_nombre || '').trim()
            if (nombre && this.partners.length && !this.partners.some((p) => p.nombre === nombre)) this.form.partner_otro = true
        },
        pickPartner(nombre) {
            const f = this.form
            // Tocar de nuevo el elegido lo quita (la pregunta es opcional).
            if (!f.partner_otro && f.partner_nombre === nombre) f.partner_nombre = ''
            else { f.partner_otro = false; f.partner_nombre = nombre }
            f.referido = f.partner_nombre.trim() !== ''
            this.$delete(this.errors, 'partner_nombre')
        },
        pickPartnerOther() {
            const f = this.form
            f.partner_nombre = ''
            f.partner_otro = !f.partner_otro
            f.referido = false
            this.$delete(this.errors, 'partner_nombre')
            if (f.partner_otro) setTimeout(() => this.$refs.partnerInput && this.$refs.partnerInput.focus(), 200)
        },
        onPartnerInput() {
            this.form.referido = this.form.partner_nombre.trim() !== ''
            this.onInput('partner_nombre')
        },

        // ── Bloques que aparecen (alto animado) ───────────────────────
        revealEnter(el, done) {
            if (this.reduceMotion || !el.animate) { done(); return }
            const h = el.scrollHeight
            el.animate([{ height: '0px', opacity: 0 }, { height: h + 'px', opacity: 1 }], { duration: 320, easing: 'cubic-bezier(.22,1,.36,1)' })
                .onfinish = () => { done(); this.keepVisible(el) }
        },
        revealLeave(el, done) {
            if (this.reduceMotion || !el.animate) { done(); return }
            const h = el.scrollHeight
            el.animate([{ height: h + 'px', opacity: 1 }, { height: '0px', opacity: 0 }], { duration: 240, easing: 'cubic-bezier(.22,1,.36,1)' })
                .onfinish = done
        },
        // Si lo que acaba de aparecer queda debajo de la barra fija (móvil), se desplaza lo justo.
        keepVisible(el) {
            const nav = this.$refs.nav
            const navTop = nav && getComputedStyle(nav).position === 'fixed' ? nav.getBoundingClientRect().top : window.innerHeight
            const r = el.getBoundingClientRect()
            const overflow = r.bottom + 16 - navTop
            if (overflow > 0) window.scrollBy({ top: Math.min(overflow, Math.max(0, r.top - 120)), behavior: this.reduceMotion ? 'auto' : 'smooth' })
        },

        // ── Barra de progreso ──────────────────────────────────────────
        setProgress(pct, instant) {
            pct = Math.max(0, Math.min(100, pct))
            if (pct === this.lastPct) return
            const forward = pct > this.lastPct && this.lastPct >= 0
            this.lastPct = pct
            if (forward && !this.reduceMotion) {
                this.planeMoving = false
                this.$nextTick(() => { void (this.$refs.plane && this.$refs.plane.offsetWidth); this.planeMoving = true })
            }
            this.countTo(pct, instant)
        },
        // El número sube contando, no salta.
        countTo(target, instant) {
            cancelAnimationFrame(this.pctRaf)
            const from = this.shownPct
            if (instant || this.reduceMotion || from === target) { this.shownPct = target; return }
            const t0 = performance.now()
            const tick = (t) => {
                const k = Math.min(1, (t - t0) / 650)
                this.shownPct = Math.round(from + (target - from) * (1 - Math.pow(1 - k, 3)))
                if (k < 1) this.pctRaf = requestAnimationFrame(tick)
            }
            this.pctRaf = requestAnimationFrame(tick)
            if (target > from) { this.pctBump = false; this.$nextTick(() => { this.pctBump = true }) }
        },
        cheer(text, ms = 2600) {
            clearTimeout(this.bubbleTimer)
            this.bubble = text
            this.bubbleKey++
            this.bubbleTimer = setTimeout(() => { this.bubble = '' }, ms)
        },
        cheerForStep(step) {
            if (this.cheered[step]) return
            this.$set(this.cheered, step, true)
            const name = this.form.nombre.trim().split(/\s+/)[0]
            const msgs = {
                2: name ? this.t('cheer.step2_name', { name }) : this.t('cheer.step2'),
                3: this.t('cheer.step3'), 4: this.t('cheer.step4'), 5: this.t('cheer.step5'),
            }
            if (msgs[step]) this.cheer(msgs[step])
        },

        // ── Navegación ─────────────────────────────────────────────────
        stepEl(n) { return this.$el.querySelector(`.nf-step[data-step="${n}"]`) },
        async goTo(target) {
            if (this.animating || target === this.step) return
            const from = this.stepEl(this.step)
            const dir = target > this.step ? 1 : -1
            const viewport = this.$refs.viewport
            this.animating = true
            this.closeCombos()
            try {
                if (!this.reduceMotion && from && from.animate) {
                    const h0 = viewport.offsetHeight
                    const exit = from.animate([{ opacity: 1, transform: 'none' }, { opacity: 0, transform: `translateX(${-28 * dir}px)` }],
                        { duration: 200, easing: 'cubic-bezier(.4,0,1,1)', fill: 'forwards' })
                    // Tope de tiempo: si el navegador demora la animación, el paso cambia igual.
                    await Promise.race([exit.finished.catch(() => {}), wait(260)])
                    this.visibleStep = target
                    await this.$nextTick()
                    exit.cancel()
                    const to = this.stepEl(target)
                    const h1 = viewport.offsetHeight
                    viewport.animate([{ height: h0 + 'px' }, { height: h1 + 'px' }], { duration: 380, easing: 'cubic-bezier(.22,1,.36,1)' })
                    Array.from(to.children).forEach((c, i) => c.style.setProperty('--i', i))
                    to.classList.add('is-entering')
                    to.animate([{ opacity: 0, transform: `translateX(${28 * dir}px)` }, { opacity: 1, transform: 'none' }],
                        { duration: 380, easing: 'cubic-bezier(.22,1,.36,1)' })
                    setTimeout(() => to.classList.remove('is-entering'), 900)
                } else {
                    this.visibleStep = target
                    await this.$nextTick()
                }

                this.step = target
                if (target === TOTAL_STEPS) this.editing = false
                this.setPageQuery(target)
                if (dir > 0) this.cheerForStep(target)

                // Subir al inicio del formulario y llevar el foco.
                const to = this.stepEl(target)
                const top = this.$refs.form.getBoundingClientRect().top + window.scrollY - (parseInt(getComputedStyle(this.$el).getPropertyValue('--nf-top'), 10) || 0) - 90
                if (window.scrollY > top) window.scrollTo({ top: Math.max(0, top), behavior: this.reduceMotion ? 'auto' : 'smooth' })
                const firstEmpty = Array.from(to.querySelectorAll('input:not([type=range]):not([type=date])')).find((i) => !i.value && i.offsetParent)
                if (this.finePointer && firstEmpty && dir > 0) firstEmpty.focus({ preventScroll: true })
                else { const h = to.querySelector('.nf-step-title'); if (h) h.focus({ preventScroll: true }) }
            } finally {
                this.animating = false
            }
            this.scheduleSave()
        },
        async next() {
            if (!this.ready || this.animating || this.submitting || this.screen !== 'form') return
            const bad = this.validateStep(this.step)
            if (bad.length) { this.focusFirstError(bad); this.scheduleSave(); return }
            if (this.isLast) { this.submit(); return }

            // Entró sin progreso (page=input): se registra esa página antes de
            // avanzar. Se manda sin esperar la respuesta: el paso no se frena por la red.
            if (this.pageLabel) {
                this.saveProgress({ page: this.pageLabel }).catch(() => {})
                this.pageLabel = null
            }
            if (this.editing) {
                // Editando desde el resumen: directo al final, salvo que un paso intermedio haya quedado incompleto.
                for (let s = this.step + 1; s < TOTAL_STEPS; s++) {
                    if (this.validateStep(s, false).length) { this.goTo(s); return }
                }
                this.goTo(TOTAL_STEPS)
                return
            }
            this.goTo(this.step + 1)
        },
        back() { if (this.step > 1) this.goTo(this.step - 1) },
        editStep(n) {
            if (this.animating) return
            this.editing = true
            this.goTo(n)
        },
        onDocKey(e) {
            if (e.key === 'Escape') this.closeCombos()
            if (e.altKey && e.key === 'ArrowLeft' && this.screen === 'form') { e.preventDefault(); this.back() }
        },
        restart() {
            try { localStorage.removeItem(this.progressLocalKey) } catch (e) { /* nada */ }
            const meta = this.form.meta
            this.form = emptyForm()
            this.form.meta = meta
            const g = this.guessPhoneCountry()
            if (g) { this.form.phone_country = g.code; this.form.phone_code = g.value }
            this.errors = {}
            this.valid = {}
            this.touched = {}
            this.cheered = {}
            this.editing = false
            this.step = 1
            this.visibleStep = 1
            this.toastVisible = false
            this.setPageQuery(1)
            this.$nextTick(() => this.$refs.nombre && this.$refs.nombre.focus())
        },

        // ── Autoguardado (igual que Formulario.vue) ───────────────────
        fullPhone() {
            const f = this.form
            return f.phone_code && f.whatsapp ? `${f.phone_code}${f.whatsapp}` : f.whatsapp
        },
        progressPayload(extra = {}) {
            const payload = {
                session_id: this.sessionId,
                email: this.form.email || null,
                whatsapp: this.fullPhone() || null,
                meta: this.form.meta || null,
                page: this.pageLabel ? this.pageLabel : String(this.step),
                current_step: this.step,
                data: this.form,
                last_activity: new Date().toISOString(),
            }
            if (Object.keys(this.errors).length) payload.errors = this.errors
            return Object.assign(payload, extra)
        },
        // Copia en el navegador en cada cambio: si se recarga antes de que la
        // API conteste (o sin red), no se pierde nada.
        saveLocal(payload) {
            try { localStorage.setItem(this.progressLocalKey, JSON.stringify(payload || this.progressPayload())) } catch (e) { /* modo privado */ }
        },
        scheduleSave() {
            if (this.finished) return
            this.saveLocal()
            if (this.saveTimer) clearTimeout(this.saveTimer)
            this.saveTimer = setTimeout(() => {
                this.saveTimer = null
                this.saveProgress().catch(() => {})
            }, SAVE_DEBOUNCE_MS)
        },
        async saveProgress(options = {}) {
            const payload = this.progressPayload(options.page !== undefined ? { page: options.page } : {})
            if (options.beacon && navigator.sendBeacon) {
                this.saveLocal(payload)
                try { navigator.sendBeacon(`${API()}intake/progress`, new Blob([JSON.stringify(payload)], { type: 'application/json' })) } catch (e) { /* queda la copia local */ }
                return
            }
            this.saveState = 'saving'
            clearTimeout(this.savedTimer)
            try {
                const res = await axios.post(`${API()}intake/progress`, payload)
                this.saveState = 'saved'
                this.savedTimer = setTimeout(() => { this.saveState = '' }, 2200)
                return res.data
            } catch (err) {
                // Sin red: queda en el navegador y se recupera de ahí.
                try { localStorage.setItem(this.progressLocalKey, JSON.stringify(payload)) } catch (e) { /* nada */ }
                this.saveState = ''
                throw err
            }
        },
        async restoreProgress() {
            // Copia local (puede ser más nueva que la del servidor).
            let local = null
            try { local = JSON.parse(localStorage.getItem(this.progressLocalKey) || 'null') } catch (e) { local = null }
            if (!(local && local.data && (local.data.nombre || local.data.email || Number(local.current_step) > 1))) local = null
            const time = (s) => { const n = Date.parse(s && s.last_activity); return Number.isNaN(n) ? 0 : n }
            try {
                const params = { session_id: this.sessionId }
                if (this.form.meta) params.meta = this.form.meta
                if (this.form.email) params.email = this.form.email
                const res = await axios.get(`${API()}intake/progress`, { params })
                const saved = res && res.data && res.data.success ? res.data.data : null
                if (saved && saved.data && !(local && time(local) > time(saved))) {
                    this.applySaved(saved.data)
                    if (saved.page === 'input') { this.pageLabel = 'input'; this.step = 1 }
                    else if (saved.page === 'thanks') this.screen = 'success'
                    else if (saved.page && !Number.isNaN(parseInt(saved.page, 10))) this.step = parseInt(saved.page, 10)
                    else if (saved.current_step) this.step = Number(saved.current_step)
                    this.step = Math.min(TOTAL_STEPS, Math.max(1, this.step || 1))
                    if (saved.page) this.setPageQuery(saved.page)
                    return true
                }
            } catch (e) {
                // 404 = no hay progreso; cualquier otro error: se prueba lo local.
            }
            if (local) {
                this.applySaved(local.data)
                this.step = Math.min(TOTAL_STEPS, Math.max(1, Number(local.current_step) || 1))
                if (local.page === 'input') this.pageLabel = 'input'
                return true
            }
            return false
        },
        applySaved(data) {
            const meta = this.form.meta
            this.form = normalizeForm(Object.assign({}, this.form, data))
            if (meta) this.form.meta = meta
            this.adjustPartnerOther()
        },
        beforeUnloadHandler() {
            if (this.finished || this.submitting) return
            this.saveProgress({ beacon: true })
        },

        // ── Envío ──────────────────────────────────────────────────────
        // Idéntico a buildIntakePayload() de Formulario.vue.
        buildIntakePayload() {
            const f = this.form
            return {
                session_id: this.sessionId,
                nombre: f.nombre.trim(),
                email: f.email.trim(),
                whatsapp: this.fullPhone(),
                pais_origen: f.pais_origen,
                tipo_programa: f.programa || null,
                edad: Number(f.edad),
                universidad: f.universidad.trim() || null,
                universidad_destino: f.universidad_destino.trim() || null,
                ciudad_destino: f.ciudad || null,
                zonas_preferidas: f.zonas_preferidas,
                zonas_cercanas: f.zonas_cercanas,
                fecha_inicio: f.fecha_inicio,
                fecha_fin: f.fecha_fin,
                presupuesto_usd: Number(f.presupuesto),
                tipo_alojamiento: f.tipo_alojamiento,
                cantidad_personas: f.cantidad_personas,
                va_en_pareja: f.va_en_pareja,
                urgencia: f.urgencia,
                en_ciudad: f.en_ciudad === true,
                tiene_pasajes: f.tiene_pasajes === true,
                es_referido: f.partner_nombre.trim() !== '',
                partner_nombre: f.partner_nombre.trim() || null,
                comentario_libre: f.comentario.trim() || null,
                meta: f.meta || null,
                idioma_menu: this.selectedMenuLanguage(),
            }
        },
        // El avión llega al final y despega; después entra la pantalla de carga.
        async takeOff() {
            this.cheer(this.t('cheer.takeoff'), 1400)
            this.planeTakeoff = false
            this.setProgress(100)
            if (this.reduceMotion) { await wait(150); return }
            await wait(850)
            this.planeTakeoff = true
            await wait(800)
        },
        async submit() {
            if (this.submitting) return
            this.submitting = true
            this.form.partner_nombre = this.form.partner_nombre.trim()
            this.form.referido = this.form.partner_nombre !== ''
            if (this.saveTimer) { clearTimeout(this.saveTimer); this.saveTimer = null }

            if (this.screen === 'form') {
                await this.takeOff()
                this.loaderMinHeight = Math.min(560, Math.max(380, this.$refs.form ? this.$refs.form.offsetHeight : 460))
            }
            // La barra se va y queda solo el logo con la carga.
            this.barHidden = true
            this.toastVisible = false
            this.screen = 'loading'
            this.loadingIdx = 0
            clearInterval(this.loadingTimer)
            this.loadingTimer = setInterval(() => { if (this.loadingIdx < 3) this.loadingIdx++ }, LOADING_MSG_MS)
            window.scrollTo({ top: 0, behavior: 'auto' })
            const t0 = Date.now()

            let ok = true
            try {
                const payload = this.buildIntakePayload()
                const res = await axios.post(`${API()}intake`, payload)
                const data = res && res.data && res.data.data ? res.data.data : null
                // El backend genera la preselección; si falla, no frena el éxito.
                if (data && data.user_intake_id) {
                    try {
                        await axios.post(`${API()}intake/${data.user_intake_id}/matching`)
                    } catch (e) {
                        console.warn('[HyF][Intake] no se pudo generar la shortlist:', e && e.message ? e.message : e)
                    }
                }
                try { localStorage.removeItem(this.progressLocalKey) } catch (e) { /* nada */ }
                try {
                    await axios.post(`${API()}intake/progress`, this.progressPayload({ whatsapp: payload.whatsapp || null, page: 'thanks', current_step: 5, is_final: true }))
                } catch (e) {
                    console.warn('[HyF][Intake] no se pudo guardar el progreso final:', e && e.message ? e.message : e)
                }
            } catch (err) {
                console.error('[HyF][Intake] error al enviar:', err)
                ok = false
            }

            const rest = LOADER_MIN_MS - (Date.now() - t0)
            if (rest > 0) await wait(rest)
            clearInterval(this.loadingTimer)

            if (ok) this.showSuccess()
            else this.showError()
        },
        showSuccess() {
            this.finished = true
            this.successName = this.form.nombre.trim().split(/\s+/)[0] || ''
            const sid = generateSessionId()
            this.sessionId = sid
            this.progressLocalKey = `hyf_form_progress_${sid}`
            try { localStorage.setItem('hyf_form_session', sid) } catch (e) { /* nada */ }
            this.setPageQuery('thanks')
            this.screen = 'success'
            this.submitting = false
            // La tarjeta de éxito entra cuando terminó de irse la carga (~400 ms).
            setTimeout(this.confetti, this.reduceMotion ? 0 : 700)
        },
        showError() {
            this.planeTakeoff = false
            this.barHidden = false
            this.screen = 'error'
            this.submitting = false
        },
        backToForm() {
            this.screen = 'form'
            this.$nextTick(() => this.setProgress(this.progressPct, true))
        },

        // ── Confeti ────────────────────────────────────────────────────
        confetti() {
            const canvas = this.$refs.confetti
            if (this.reduceMotion || !canvas) return
            const host = canvas.parentElement
            const dpr = Math.min(window.devicePixelRatio || 1, 2)
            const W = host.offsetWidth
            const H = host.offsetHeight
            canvas.width = W * dpr
            canvas.height = H * dpr
            const ctx = canvas.getContext('2d')
            ctx.scale(dpr, dpr)
            const colors = ['#ff7a00', '#ff9a3d', '#ffd6ad', '#1f9d6c', '#1d1a17', '#ffc590']
            const parts = Array.from({ length: 150 }, () => ({
                x: W / 2 + (Math.random() - 0.5) * 80, y: 110,
                vx: (Math.random() - 0.5) * 13, vy: -Math.random() * 11 - 4,
                w: 6 + Math.random() * 6, h: 8 + Math.random() * 8,
                r: Math.random() * Math.PI, vr: (Math.random() - 0.5) * 0.3,
                c: colors[(Math.random() * colors.length) | 0], life: 1,
            }))
            const start = performance.now()
            const frame = (t) => {
                if (!this.$refs.confetti) return
                const elapsed = t - start
                ctx.clearRect(0, 0, W, H)
                parts.forEach((p) => {
                    p.vy += 0.28; p.vx *= 0.985; p.vy *= 0.985
                    p.x += p.vx; p.y += p.vy; p.r += p.vr
                    if (elapsed > 2200) p.life = Math.max(0, p.life - 0.02)
                    ctx.save()
                    ctx.globalAlpha = p.life
                    ctx.translate(p.x, p.y)
                    ctx.rotate(p.r)
                    ctx.fillStyle = p.c
                    ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h * Math.abs(Math.cos(p.r * 2)))
                    ctx.restore()
                })
                if (elapsed < 3800) requestAnimationFrame(frame)
                else ctx.clearRect(0, 0, W, H)
            }
            requestAnimationFrame(frame)
        },
    },
}
